// Project persistence: autosave the working draft, keep named snapshots in
// localStorage, and import/export the full project as a portable .json file.
// Saves the entire ProjectBrief, so every field (special conditions, prepared-by,
// advance disbursement, DRB config, narrative, drawings…) round-trips.

import type { ProjectBrief } from "../contracts/types";

const CURRENT = "cg:current";
const NAMED = "cg:projects"; // { [name]: { savedAt, brief } }
const SCHEMA = 2;

interface SavedEntry {
  savedAt: string;
  schema: number;
  brief: ProjectBrief;
}
type NamedMap = Record<string, SavedEntry>;

function readNamed(): NamedMap {
  try {
    return JSON.parse(localStorage.getItem(NAMED) || "{}") as NamedMap;
  } catch {
    return {};
  }
}
function writeNamed(m: NamedMap) {
  localStorage.setItem(NAMED, JSON.stringify(m));
}

/** The raw named-projects map (for syncing up to the account). */
export function getNamedMap(): NamedMap {
  return readNamed();
}
/** Merge server-synced projects into the local map — newer savedAt wins. */
export function mergeNamedMap(incoming: NamedMap | null | undefined): void {
  if (!incoming) return;
  const m = readNamed();
  for (const [name, entry] of Object.entries(incoming)) {
    if (!entry?.brief) continue;
    const cur = m[name];
    if (!cur || (entry.savedAt || "") >= (cur.savedAt || "")) m[name] = entry;
  }
  writeNamed(m);
}

/** Autosaved working draft (survives reloads). */
export function loadCurrent(): ProjectBrief | null {
  try {
    const raw = localStorage.getItem(CURRENT);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as SavedEntry;
    return parsed?.brief ?? null;
  } catch {
    return null;
  }
}
export function saveCurrent(brief: ProjectBrief) {
  try {
    localStorage.setItem(CURRENT, JSON.stringify({ savedAt: nowIso(), schema: SCHEMA, brief }));
  } catch {
    /* quota / private mode — ignore */
  }
}

export function listProjects(): { name: string; savedAt: string }[] {
  const m = readNamed();
  return Object.entries(m)
    .map(([name, e]) => ({ name, savedAt: e.savedAt }))
    .sort((a, b) => (a.savedAt < b.savedAt ? 1 : -1));
}
export function saveNamed(name: string, brief: ProjectBrief) {
  const m = readNamed();
  m[name] = { savedAt: nowIso(), schema: SCHEMA, brief };
  writeNamed(m);
}
export function loadNamed(name: string): ProjectBrief | null {
  return readNamed()[name]?.brief ?? null;
}
export function deleteNamed(name: string) {
  const m = readNamed();
  delete m[name];
  writeNamed(m);
}

/** Download the project as a .json file. */
export function exportJson(brief: ProjectBrief) {
  const safe = (brief.projectName || "project").replace(/[^\w\d]+/g, "_").replace(/^_+|_+$/g, "").slice(0, 50) || "project";
  const payload = { savedAt: nowIso(), schema: SCHEMA, brief };
  const blob = new Blob([JSON.stringify(payload, null, 2)], { type: "application/json" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `${safe}.contract.json`;
  a.click();
  setTimeout(() => URL.revokeObjectURL(url), 2000);
}

/** Read a .json project file back into a ProjectBrief. */
export async function importJson(file: File): Promise<ProjectBrief> {
  const text = await file.text();
  const parsed = JSON.parse(text);
  const brief = (parsed?.brief ?? parsed) as ProjectBrief;
  if (!brief || typeof brief !== "object" || !("standard" in brief)) {
    throw new Error("Not a valid contract project file.");
  }
  return brief;
}

// Date.now via a stamped helper (avoids failures where the platform blocks it).
function nowIso(): string {
  try {
    return new Date().toISOString();
  } catch {
    return "";
  }
}
